import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Spinner, InputGroup, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FaSearch,
  FaCalendarAlt,
  FaTimes,
  FaExclamationTriangle,
  FaSync,
  FaPlus,
  FaStar
} from 'react-icons/fa';
import { getJobs } from '../../services/jobService';
import { toast } from 'react-toastify';
import './JobList.css';

/**
 * Environment-aware logging helpers
 */
const log = () => {};
const error = (...args) => console.error(...args); // Always log errors

/**
 * Enhanced JobList Component - 3 Column Region Layout
 *
 * Displays jobs in a compact 3-column layout organized by region (OC, LA, IE)
 *
 * @param {Function} onSelectJob - Optional callback when a job is selected
 * @param {string} customerFilter - Optional customer name to filter jobs by
 */
const JobList = ({ onSelectJob, customerFilter }) => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Define regions for job organization
  const regions = [
    { code: 'OC', name: 'Orange County' },
    { code: 'LA', name: 'Los Angeles' },
    { code: 'IE', name: 'Inland Empire' }
  ];
  
  /**
   * Format job number to display single digit job numbers with leading zero
   * Handles the specific format: [2 Month Letters][Job Number][2 Year Digits]
   * Examples:
   * - MY125 → MY0125 (May, Job #1, Year 25)
   * - MY225 → MY0225 (May, Job #2, Year 25)  
   * - MY1025 → MY1025 (May, Job #10, Year 25 - no change needed)
   * - MY0125 → MY0125 (already formatted - no change)
   * 
   * @param {string|number} jobNumber - The original job number
   * @returns {string} Formatted job number with leading zero for single digit job numbers
   */
  const formatJobNumber = (jobNumber) => {
    if (!jobNumber) return '';
    
    const jobStr = String(jobNumber).trim();
    
    // Handle pure single digits (1, 2, 3, etc.)
    if (/^\d$/.test(jobStr)) {
      return `0${jobStr}`;
    }
    
    // Handle the specific format: [2 Letters][Job Number][25]
    // Match pattern: 2 letters + digits + 25 at the end
    const jobPatternMatch = jobStr.match(/^([A-Za-z]{2})(\d+)(25)$/);
    
    if (jobPatternMatch) {
      const [, monthCode, jobNum, year] = jobPatternMatch;
      
      // Check if job number is single digit and doesn't already have leading zero
      if (jobNum.length === 1) {
        return `${monthCode}0${jobNum}${year}`;
      }
      
      // If job number already has leading zero or is multi-digit, return as-is
      return jobStr;
    }
    
    // For any other format, return unchanged
    return jobStr;
  };
  
  /**
   * Load jobs from API with error handling and loading states
   */
  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    setIsRefreshing(true);
    
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;

      log('[JobList] Loading jobs with params:', params);
      const data = await getJobs(params);

      if (Array.isArray(data)) {
        setJobs(data);
        setFilteredJobs(data);
        log('[JobList] Jobs loaded successfully:', data.length);
      } else {
        error('[JobList] Invalid data format received:', data);
        setError('Invalid data received from server');
        toast.error('Invalid data received from server');
      }
    } catch (err) {
      error('[JobList] Error loading jobs:', err);
      
      // Handle specific error types
      if (err.message.includes('Authentication required') || 
          err.message.includes('session has expired')) {
        setError('Your session has expired. Please log in again.');
        toast.error('Your session has expired. Please log in again.');
        // Optionally redirect to login
      } else if (err.message.includes('permission')) {
        setError('You do not have permission to view jobs.');
        toast.error('You do not have permission to view jobs.');
      } else if (err.message.includes('network') || err.message.includes('connect')) {
        setError('Network connection error. Please check your connection.');
        toast.error('Network connection error. Please check your connection.');
      } else {
        setError(err.message || 'Failed to load jobs. Please try again.');
        toast.error(err.message || 'Failed to load jobs. Please try again.');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [statusFilter]);
  
  /**
   * Load jobs on component mount or when filters change
   */
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);
  
  /**
   * Filter jobs based on search term, status filter, and customer filter
   * Automatically excludes cancelled jobs unless specifically filtering for them
   * Note: Search uses original job_number to ensure compatibility with both formatted and unformatted numbers
   */
  useEffect(() => {
    let filtered = jobs;

    // Filter out cancelled jobs unless specifically looking for them
    if (statusFilter !== 'cancelled') {
      filtered = filtered.filter(job => job.status !== 'cancelled');
    }

    // Apply customer filter if provided
    if (customerFilter && filtered.length) {
      const customerLower = customerFilter.toLowerCase();
      filtered = filtered.filter(job =>
        job.customer_name && job.customer_name.toLowerCase() === customerLower
      );
    }

    // Apply search filter if search term exists
    if (searchTerm && filtered.length) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(job => {
        const originalJobNumber = String(job.job_number || '').toLowerCase();
        const formattedJobNumber = formatJobNumber(job.job_number).toLowerCase();

        // Search both original and formatted job numbers to handle user expectations
        return originalJobNumber.includes(searchLower) ||
               formattedJobNumber.includes(searchLower) ||
               (job.customer_name && job.customer_name.toLowerCase().includes(searchLower)) ||
               (job.job_scope && job.job_scope.toLowerCase().includes(searchLower)) ||
               (job.address && job.address.toLowerCase().includes(searchLower));
      });
    }

    setFilteredJobs(filtered);
  }, [searchTerm, jobs, statusFilter, customerFilter]);
  
  /**
   * Get jobs filtered by specific region code
   * @param {string} regionCode - The region code to filter by (OC, LA, IE)
   * @returns {Array} Array of jobs for the specified region
   */
  const getJobsByRegion = (regionCode) => {
    return filteredJobs.filter(job => job.region === regionCode);
  };
  
  /**
   * Handle search input changes with real-time filtering
   * @param {Event} e - Input change event
   */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  /**
   * Clear the search input and reset filtering
   */
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  /**
   * Format date string for display, avoiding timezone conversion issues
   * @param {string} dateString - ISO date string from API
   * @returns {string|null} Formatted date string or null if no date
   */
  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Parse the ISO date string (YYYY-MM-DD) as local date to avoid timezone conversion
      const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
      const localDate = new Date(year, month - 1, day); // month is 0-indexed
      
      const options = { month: 'short', day: 'numeric' };
      return localDate.toLocaleDateString(undefined, options);
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return null;
    }
  };
  
  /**
   * Truncate text to specified length with ".." suffix if too long
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length before truncation
   * @returns {string} Truncated text with ".." or original if short enough
   */
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    const trimmed = text.trim();
    if (trimmed.length <= maxLength) return trimmed;
    return trimmed.substring(0, maxLength) + '..';
  };

  /**
   * Get company name for a job, truncated to 20 characters
   * @param {Object} job - Job object from API
   * @returns {string} Truncated company name
   */
  const getCompanyDisplay = (job) => {
    const company = job.customer_name || '';
    return truncateText(company, 20);
  };

  /**
   * Get address for a job, truncated to 65 characters
   * @param {Object} job - Job object from API
   * @returns {string} Truncated address
   */
  const getAddressDisplay = (job) => {
    const address = job.address || '';
    return truncateText(address, 65);
  };
  
  /**
   * Get formatted date display with calendar icon for scheduled jobs
   * @param {Object} job - Job object from API
   * @returns {JSX.Element|null} Formatted date component or null if no date
   */
  const getDateDisplay = (job) => {
    const formattedDate = formatDate(job.scheduled_date);
    if (formattedDate) {
      return (
        <span className="text-success">
          {formattedDate}
        </span>
      );
    }
    return null; // Return null for empty date column when not scheduled
  };

  /**
   * Generate status badge with appropriate color based on job status
   * @param {Object} job - Job object from API
   * @returns {JSX.Element} Status badge component with text
   */
  const getStatusDot = (job) => {
    const status = job.status;
    let bgColor = '#6c757d'; // Default grey for unknown/unscheduled
    let textColor = '#ffffff';

    // Map status values to appropriate colors
    switch(status) {
      case 'scheduled':
        bgColor = '#28a745'; // Green - ready to go
        textColor = '#ffffff';
        break;
      case 'waiting_for_parts':
        bgColor = '#ffc107'; // Yellow - waiting
        textColor = '#212529';
        break;
      case 'on_hold':
        bgColor = '#dc3545'; // Red - blocked
        textColor = '#ffffff';
        break;
      case 'completed':
        bgColor = '#007bff'; // Blue - finished
        textColor = '#ffffff';
        break;
      case 'cancelled':
        bgColor = '#6c757d'; // Grey - inactive
        textColor = '#ffffff';
        break;
      case 'unscheduled':
      default:
        bgColor = '#6c757d'; // Grey - not scheduled
        textColor = '#ffffff';
        break;
    }

    // Format status text for display
    let statusText;
    switch(status) {
      case 'scheduled':
        statusText = 'Scheduled';
        break;
      case 'waiting_for_parts':
        statusText = 'Waiting';
        break;
      case 'on_hold':
        statusText = 'On Hold';
        break;
      case 'completed':
        statusText = 'Completed';
        break;
      case 'cancelled':
        statusText = 'Cancelled';
        break;
      case 'unscheduled':
      default:
        statusText = 'Unscheduled';
        break;
    }

    return (
      <span
        className="status-badge"
        style={{
          fontSize: '0.62rem',
          fontWeight: '600',
          padding: '2px 5px',
          borderRadius: '6px',
          backgroundColor: bgColor,
          color: textColor,
          border: `1px solid ${bgColor}`,
          display: 'inline-block',
          whiteSpace: 'nowrap',
          lineHeight: '1.1'
        }}
      >
        {statusText}
      </span>
    );
  };
  
  /**
   * Render a complete region column with header, job table, and empty state
   * @param {Object} region - Region object with code and name
   * @returns {JSX.Element} Complete region column component
   */
  const renderRegionColumn = (region) => {
    const regionJobs = getJobsByRegion(region.code);
    
    return (
      <div key={region.code} className="col-md-4 region-column">
        {/* Region Header with job count */}
        <div className="region-header">
          <h6 className="mb-2 text-center font-weight-bold">
            {region.name} ({region.code})
            <span className="badge badge-secondary ms-2">{regionJobs.length}</span>
          </h6>
        </div>
        
        {/* Jobs Table or Empty State */}
        {regionJobs.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <small>No jobs in this region</small>
          </div>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="compact-job-table mb-0">
              <thead>
                <tr>
                  <th className="job-num-col">Job #</th>
                  <th className="company-col">Company</th>
                  <th className="address-col">Address</th>
                  <th className="date-col">Date</th>
                  <th className="status-col"></th>
                </tr>
              </thead>
              <tbody>
                {regionJobs.map(job => {
                  const isJobCancelled = job.status === 'cancelled';
                  
                  return (
                    <tr 
                      key={job.id} 
                      className={`compact-job-row ${isJobCancelled ? 'cancelled-job-row' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (onSelectJob) {
                          onSelectJob(job);
                        }
                      }}
                    >
                      {/* Job Number Column with Link - Now displays formatted job number */}
                      <td className="job-num-cell" data-label="Job #">
                        <Link
                          to={`/jobs/${job.id}`}
                          className="job-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {formatJobNumber(job.job_number)}
                        </Link>
                        {job.is_special && (
                          <FaStar
                            className="ms-1 text-warning"
                            style={{ fontSize: '0.7em' }}
                            title="Special Job"
                          />
                        )}
                      </td>
                      
                      {/* Company Column */}
                      <td className="company-cell" data-label="Company">
                        <Link
                          to={`/jobs/${job.id}`}
                          className="company-link"
                          onClick={(e) => e.stopPropagation()}
                          title={job.customer_name}
                        >
                          {getCompanyDisplay(job)}
                        </Link>
                      </td>

                      {/* Address Column */}
                      <td className="address-cell" data-label="Address">
                        <span title={job.address}>
                          {getAddressDisplay(job)}
                        </span>
                      </td>

                      {/* Date Column with Calendar Icon */}
                      <td className="date-cell" data-label="Date">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          {!isJobCancelled && getDateDisplay(job) ? (
                            <Link
                              to={`/schedule/job/${job.id}`}
                              className="date-link"
                              onClick={(e) => e.stopPropagation()}
                              title="Schedule Job"
                            >
                              {getDateDisplay(job)}
                            </Link>
                          ) : (
                            getDateDisplay(job)
                          )}
                          {!isJobCancelled && (
                            <Link to={`/schedule/job/${job.id}`} style={{ display: 'inline-flex' }}>
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="compact-btn"
                                onClick={(e) => e.stopPropagation()}
                                title="Schedule Job"
                                style={{
                                  padding: '0.2rem 0.3rem',
                                  lineHeight: 1,
                                  border: 'none'
                                }}
                              >
                                <FaCalendarAlt style={{ fontSize: '0.75rem' }} />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </td>

                      {/* Status Column - Removed calendar, keeping status dot */}
                      <td className="status-cell">
                        <div className="status-dot-container">
                          {getStatusDot(job)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    );
  };
  
  /**
   * Render loading state with spinner
   */
  if (loading && !isRefreshing) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p>Loading jobs...</p>
      </div>
    );
  }
  
  return (
    <div className="job-list-container">
      {/* Page Header - Title and Controls in Line */}
      <div className="job-list-page-header">
        <div className="job-list-title-row d-flex justify-content-between align-items-center">
          <h4>Jobs by Region</h4>
          <Link to="/jobs/new-special">
            <Button
              variant="warning"
              size="sm"
              className="d-flex align-items-center gap-2"
              style={{ fontWeight: 600 }}
            >
              <FaStar />
              Add Special Job
            </Button>
          </Link>
        </div>

        {/* Controls in a single row */}
        <div className="controls-inline">
          {/* Search Input */}
          <div className="control-item">
            <InputGroup size="sm" className="search-group">
              <Form.Control
                type="text"
                placeholder="Search job..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={clearSearch}
                  size="sm"
                  title="Clear search"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Status Filter */}
          <div className="control-item">
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by job status"
              className="status-filter"
            >
              <option value="">By Region</option>
              <option value="unscheduled">Unscheduled</option>
              <option value="scheduled">Scheduled</option>
              <option value="waiting_for_parts">Waiting for Parts</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
          </div>

          {/* Refresh Button */}
          <div className="control-item">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadJobs}
              disabled={isRefreshing}
              className="refresh-btn"
              title="Refresh job list"
            >
              <FaSync className={isRefreshing ? 'spin me-2' : 'me-2'} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content Area with Proper Padding */}
      <div className="job-list-content">
        {/* Error State */}
        {error ? (
          <div className="error-state">
            <FaExclamationTriangle className="me-2" />
            <p>{error}</p>
            <Button 
              variant="outline-primary" 
              onClick={loadJobs} 
              size="sm"
              className="mt-2"
            >
              Try again
            </Button>
          </div>
        ) : (
          /* Region Columns */
          <div className="container-fluid px-3">
            <div className="row g-3">
              {regions.map(region => renderRegionColumn(region))}
            </div>
          </div>

        )}
        
        {/* Empty State when no jobs found */}
        {filteredJobs.length === 0 && !loading && !error && (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p>No jobs found</p>
            {(searchTerm || statusFilter) && (
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Footer with Job Counts */}
      {filteredJobs.length > 0 && (
        <div className="list-footer">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Showing {filteredJobs.length} of {jobs.length} jobs
              {(statusFilter || searchTerm) && ' (filtered)'}
            </small>
            <small className="text-muted">
              OC: {getJobsByRegion('OC').length} | 
              LA: {getJobsByRegion('LA').length} | 
              IE: {getJobsByRegion('IE').length}
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobList;
